import { ROLES } from '../auth';
import { ClimateActionPage } from '../features/climateAction';
import ClimateManagementPage from '../features/climateManagement/components/ClimateManagementPage';
import DataInputPage from '../features/dataInput/components/DataInputPage';
import { EmissionFactorPage } from '../features/emissionFactor';
import { Scope1Page, Scope2Page, Scope3Page } from '../features/emissions';
import OfficePage from '../features/office/components/OfficePage';
import { ReportsPage } from '../features/reports';
import SettingsPage from '../features/settings/components/SettingsPage';
import DashboardPage from '../pages/DashboardPage';
import ForbiddenPage from '../pages/Forbidden403Page';

export const SECTIONS = {
  OVERVIEW: 'Overview',
  EMISSIONS: 'Emissions by scope',
  DATA: 'Data management',
  MASTER_DATA: 'Master data',
  SYSTEM: 'System',
};

export const SECTION_LABEL_KEYS = {
  [SECTIONS.OVERVIEW]: 'nav.sections.overview',
  [SECTIONS.EMISSIONS]: 'nav.sections.emissions',
  [SECTIONS.DATA]: 'nav.sections.dataManagement',
  [SECTIONS.MASTER_DATA]: 'nav.sections.masterData',
  [SECTIONS.SYSTEM]: 'nav.sections.system',
};

/** Grouped sidebar boxes: sections inside a box render as one colored card. */
export const NAV_BOXES = [
  { id: 'dashboard', labelKey: 'nav.boxes.dashboard', sections: [SECTIONS.OVERVIEW, SECTIONS.EMISSIONS] },
  { id: 'actions', labelKey: 'nav.boxes.actions', sections: [SECTIONS.DATA, SECTIONS.MASTER_DATA] },
  { id: 'system', labelKey: 'nav.boxes.system', sections: [SECTIONS.SYSTEM] },
];

export const DEFAULT_ROUTE = '/overview';

export const HOME_LABEL = 'Home';

export const ROUTES = [
  { path: '/overview', label: 'Dashboard', navKey: 'nav.dashboard', breadcrumbKey: 'dashboard.title', icon: 'dashboard', section: SECTIONS.OVERVIEW,
    roles: [ROLES.VIEWER, ROLES.ADMIN], element: <DashboardPage /> },
  { path: '/climate-action', label: 'Climate Action', navKey: 'nav.climateAction', breadcrumbKey: 'climateAction.title', icon: 'target', section: SECTIONS.OVERVIEW,
    roles: [ROLES.VIEWER, ROLES.ADMIN], element: <ClimateActionPage /> },

  { path: '/scope1', label: 'Scope 1 · Direct Emissions', navKey: 'nav.scope1', breadcrumbKey: 'scopeEmissions.scope1.title', icon: 'flame', section: SECTIONS.EMISSIONS,
    roles: [ROLES.VIEWER, ROLES.ADMIN], element: <Scope1Page /> },
  { path: '/scope2', label: 'Scope 2 · Indirect Emissions from Energy Use', navKey: 'nav.scope2', breadcrumbKey: 'scopeEmissions.scope2.title', icon: 'zap', section: SECTIONS.EMISSIONS,
    roles: [ROLES.VIEWER, ROLES.ADMIN], element: <Scope2Page /> },
  { path: '/scope3', label: 'Scope 3 · Upstream Emissions', navKey: 'nav.scope3', breadcrumbKey: 'scopeEmissions.scope3.title', icon: 'globe', section: SECTIONS.EMISSIONS,
    roles: [ROLES.VIEWER, ROLES.ADMIN], element: <Scope3Page /> },

  { path: '/data-input', label: 'Data Input & Upload', navKey: 'nav.dataInput', breadcrumbKey: 'dataInput.title', icon: 'upload', section: SECTIONS.DATA,
    roles: [ROLES.ADMIN], element: <DataInputPage /> },
  { path: '/reports', label: 'Reports', navKey: 'nav.reports', breadcrumbKey: 'reports.title', icon: 'file-text', section: SECTIONS.DATA,
    roles: [ROLES.ADMIN], element: <ReportsPage /> },

  { path: '/emission-factors', label: 'Emission Factor', navKey: 'nav.emissionFactor', breadcrumbKey: 'emissionFactor.title', icon: 'sliders', section: SECTIONS.MASTER_DATA,
    roles: [ROLES.ADMIN], element: <EmissionFactorPage /> },
  { path: '/offices', label: 'Company locations', navKey: 'nav.office', breadcrumbKey: 'office.title', icon: 'package', section: SECTIONS.MASTER_DATA,
    roles: [ROLES.ADMIN], element: <OfficePage /> },
  { path: '/climate-management', label: 'Climate Management', navKey: 'nav.climateManagement', breadcrumbKey: 'climateAction.management.title', icon: 'chart', section: SECTIONS.MASTER_DATA,
    roles: [ROLES.ADMIN], element: <ClimateManagementPage /> },

  { path: '/settings', label: 'Settings', navKey: 'nav.settings', breadcrumbKey: 'breadcrumb.settings', icon: 'settings', section: SECTIONS.SYSTEM,
    roles: [ROLES.ADMIN], element: <SettingsPage /> },

  { path: '/403', label: 'Access denied', hidden: true, element: <ForbiddenPage /> },
];

export function navSectionsForRole(hasRoleFn) {
  const sections = Object.values(SECTIONS)
    .map(section => ({
      label: section,
      labelKey: SECTION_LABEL_KEYS[section],
      items: ROUTES.filter(r => !r.hidden && r.section === section && hasRoleFn(r.roles)),
    }))
    .filter(section => section.items.length > 0);

  const groups = [];
  const consumed = new Set();
  NAV_BOXES.forEach(box => {
    const boxSections = sections.filter(section => box.sections.includes(section.label));
    const items = boxSections.flatMap(section => section.items);
    if (items.length === 0) return;
    boxSections.forEach(section => consumed.add(section.label));
    groups.push({ id: box.id, labelKey: box.labelKey, items });
  });
  sections.forEach(section => {
    if (!consumed.has(section.label)) groups.push(section);
  });
  return groups;
}

export function breadcrumbForPath(pathname) {
  const home = { label: HOME_LABEL, labelKey: 'breadcrumb.home', path: DEFAULT_ROUTE };
  const route = ROUTES.find(r => r.path === pathname);
  return route ? [home, { label: route.label, labelKey: route.breadcrumbKey }] : [home];
}
